/**
 * studentLiveEndViewModel — le rapport du jour (`GET api/v1/live-sessions/{id}/today-live-report`,
 * `LiveReportResource`) en props de `<StudentLiveEndPage>`, par les adaptateurs du rapport du parent
 * (l'ancien écran montrait à l'élève le même rapport qu'au parent).
 *
 * Pur. `report: null` veut dire « pas de rapport » : la page dit la fin seule.
 */
import {
    toUILiveSessionReport,
    type ApiLiveReportListItem,
} from "../../../adapters/parent/LiveReports/liveReportListAdapter"
import {
    toUILiveSessionReportDetails,
    type ApiLiveReportDetails,
} from "../../../adapters/parent/LiveReports/liveReportDetailsAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    type UILiveSessionReport,
    type UILiveSessionReportActivity,
    type UILiveSessionReportDetails,
} from "../../../ui/types"
import { type UILiveEndScores } from "../../../ui/types/studentLive"

export type LiveEndModel = {
    report: UILiveSessionReport | null
    scores?: UILiveEndScores
    validatedActivities: UILiveSessionReportActivity[]
    todoActivities: UILiveSessionReportActivity[]
    /** Le mot de Zoé, ou le message d'absence : ce que l'ancien rapport de fin montrait. */
    heroMessage?: string
    chapterSections: UILiveSessionReportDetails["chapterSections"]
    conceptGroups?: UILiveSessionReportDetails["conceptGroups"]
}

/** Un rapport vide (rien d'écrit encore ce jour-là) n'est pas un rapport. */
function isReport(raw: unknown): raw is ApiLiveReportListItem & ApiLiveReportDetails {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return false
    const report = raw as { id?: unknown; hash?: unknown }
    return report.id != null || report.hash != null
}

export function liveEndModel(raw: unknown, subjects: ApiSchoolSubject[] | null | undefined): LiveEndModel {
    if (!isReport(raw)) return { report: null, validatedActivities: [], todoActivities: [], chapterSections: [] }
    const details = toUILiveSessionReportDetails(raw, subjects)
    return {
        report: toUILiveSessionReport(raw, subjects),
        ...(details?.scores != null ? { scores: details.scores } : {}),
        validatedActivities: details?.validatedActivities ?? [],
        todoActivities: details?.todoActivities ?? [],
        ...(details?.heroMessage != null ? { heroMessage: details.heroMessage } : {}),
        chapterSections: details?.chapterSections ?? [],
        ...(details?.conceptGroups != null ? { conceptGroups: details.conceptGroups } : {}),
    }
}
